import { isLive, overdueKind } from "@talentkontor/domain";
import type { Pill, Tone } from "../../../model/invoice-view";
import { initials } from "../../../lib/format";
import { t, ta } from "../../../lib/sprache";
import { beitragAktiv } from "../kooperationen/beitraege-model";
import {
  TEILWEISE, creatorFrist, invoicePillFallback, isBrandInvoice, isCreatorInvoice, monatStorniert, type InvoiceEntry, type KoopZeile, type MonatInfo,
} from "../kooperationen/model";
import type { ContractLite, DealFull, InvoiceRow, PeriodFull } from "../kooperationen/typen";
import type { ContractListRow, SignerRow } from "../vertraege/daten";
import { nextStep, roleComplete, signerViews, statusPill as vertragsPill } from "../vertraege/modell";

/**
 * Hub (Inhaber 21.10.2026, „ja Hub bauen“): ein Ort für die Zusammenhänge Marke ↔ Kampagne ↔ Kooperation ↔ Creator, je
 * Kooperation der Stand von Vertrag, Content und Rechnung als drei Pillen. Reine Funktionen ohne Datenbank; die Regeln
 * nutzen die vorhandenen Modelle (Verträge: statusPill/signerViews/nextStep, Content: Beiträge und Fristen der Fachlogik,
 * Rechnungen: Rechnungsansicht und Monatsabrechnung der Kooperationen). Getestet in apps/web/test/hub-model.test.ts.
 */

// ── Stand einer Pille ─────────────────────────────────────────────────────────

/** ✓ erledigt, ◷ offen, ! hängt bzw. überfällig, – noch nicht dran */
export type StandArt = "ok" | "laeuft" | "haengt" | "leer";

export interface Stand {
  art: StandArt;
  label: string;
  /** Sprungziel der Pille, wenn es eindeutig ist (ein Vertrag, eine Rechnung, ein Beitrag); sonst null = die Kooperation */
  ziel: string | null;
}

export const STAND_TON: Record<StandArt, Tone> = { ok: "success", laeuft: "warning", haengt: "error", leer: "neutral" };
export const STAND_ZEICHEN: Record<StandArt, string> = { ok: "✓", laeuft: "◷", haengt: "!", leer: "–" };

export function standPill(s: Pick<Stand, "art" | "label">): Pill {
  return { label: s.label, tone: STAND_TON[s.art], symbol: STAND_ZEICHEN[s.art] };
}

const stand = (art: StandArt, label: string, ziel: string | null = null): Stand => ({ art, label, ziel });

/** Reihenfolge, wenn mehrere Teile einer Pille verschiedene Stände haben: Probleme zuerst. */
const RANG: Record<StandArt, number> = { haengt: 0, laeuft: 1, ok: 2, leer: 3 };

// ── Vertrag ───────────────────────────────────────────────────────────────────

/** Vertragsliste mit Unterzeichnern (loadVertragsListe); ohne Leserecht null, dann zählt nur der Vertragsstatus. */
export interface VertragsLage {
  rows: ReadonlyMap<string, ContractListRow>;
  signers: readonly SignerRow[];
  portalTalents: ReadonlySet<string>;
}

function einVertrag(c: ContractLite, lage: VertragsLage | null | undefined, now: number, today: string, selbst: boolean): Stand {
  const ziel = `/agentur/vertraege/${c.id}`;
  const row = lage?.rows.get(c.id);
  if (row) {
    const p = vertragsPill(row, today);
    if (row.origin === "pdf_upload" && row.status === "draft") return stand("haengt", p.label, ziel);
    if (row.status === "signed") return stand("ok", t("Unterschrieben", "Signed"), ziel);
    if (row.status === "draft") return stand("laeuft", t("Entwurf", "Draft"), ziel);
    const views = signerViews(row, lage!.signers, now, lage!.portalTalents);
    const step = nextStep(row, views);
    if (step.kind === "declined") return stand("haengt", t("Abgelehnt", "Declined"), ziel);
    if (step.kind === "resend") return stand("haengt", t("Link abgelaufen", "Link expired"), ziel);
    if (step.kind === "countersign") return stand("laeuft", t("Gegenzeichnen", "Countersign"), ziel);
    // Wer noch unterschreiben muss: Marke und/oder Creator (im Creator-Konto unterschreibt das Konto selbst als „Agentur“)
    const marke = views.some((v) => v.role === "brand") && !roleComplete(row, views, "brand");
    const creator = !selbst && views.some((v) => v.role === "talent") && !roleComplete(row, views, "talent");
    const label = marke && creator ? t("Bei Marke & Creator", "With brand & creator") : marke ? t("Bei Marke", "With brand") : creator ? t("Bei Creator", "With creator") : t("Zur Unterschrift", "Awaiting signature");
    return stand("laeuft", label, ziel);
  }
  if (c.status === "signed") return stand("ok", t("Unterschrieben", "Signed"), ziel);
  if (c.status === "draft") return stand("laeuft", t("Entwurf", "Draft"), ziel);
  return stand("laeuft", c.status === "partially_signed" ? t("Teilweise unterschrieben", "Partially signed") : t("Zur Unterschrift", "Awaiting signature"), ziel);
}

/**
 * Vertrag der Kooperation: unterschrieben ✓; Entwurf, versendet oder teilweise unterschrieben ◷ (bei Marke, bei Creator,
 * Gegenzeichnen); abgelehnt, Link abgelaufen oder PDF fehlt !; ohne Vertrag – bzw. ◷ „Fehlt“, solange die Kooperation im
 * Arbeitsstand „Vertrag“ steht (dieselbe Regel wie der nächste Schritt der Kooperation: „Vertrag anlegen“).
 * Aufgehobene Verträge zählen nicht. Mehrere Verträge (Rahmenvertrag): Probleme zuerst, ✓ nur, wenn alle unterschrieben sind.
 */
export function vertragsStand(d: { arbeitsstand: string; vertraege: readonly ContractLite[]; lage?: VertragsLage | null; now: number; today: string; selbst?: boolean }): Stand {
  const gesehen = new Set<string>();
  const wirksam = d.vertraege.filter((c) => c.status !== "void" && !gesehen.has(c.id) && gesehen.add(c.id));
  if (!wirksam.length) return d.arbeitsstand === "contract" ? stand("laeuft", t("Fehlt", "Missing")) : stand("leer", t("Kein Vertrag", "No contract"));
  const staende = wirksam.map((c) => einVertrag(c, d.lage, d.now, d.today, !!d.selbst));
  const erster = [...staende].sort((a, b) => RANG[a.art] - RANG[b.art])[0]!;
  // Sprungziel nur, wenn eindeutig: ein einziger Vertrag oder genau einer, der hängt
  const haengend = staende.filter((s) => s.art === "haengt");
  const ziel = wirksam.length === 1 ? erster.ziel : haengend.length === 1 ? haengend[0]!.ziel : null;
  return { ...erster, ziel };
}

// ── Content ───────────────────────────────────────────────────────────────────

/** Beitrag einer Kooperation, soweit der Hub ihn braucht (deal_deliverables bzw. talent_deal_deliverables). */
export interface BeitragLite {
  id: string;
  deal_id: string;
  period_id: string | null;
  status: "planned" | "draft_submitted" | "changes_requested" | "approved" | "live" | "cancelled";
  draft_due: string | null;
  publish_on: string | null;
}

/**
 * Content einer Kooperation ohne Einzelbeiträge aus ihrem Arbeitsstand: Konzept, Produktion und Freigabe sind in Arbeit, ab der
 * Freigabe ist der Content fertig (danach folgen Reporting und Abrechnung, die den Content nicht mehr betreffen).
 */
const CONTENT_AUS_STAND: Record<string, { art: StandArt; label: () => string }> = {
  concept: { art: "laeuft", label: () => t("Konzept", "Concept") },
  production: { art: "laeuft", label: () => t("In Produktion", "In production") },
  content_approval: { art: "laeuft", label: () => t("In Prüfung", "In review") },
  content_approved: { art: "ok", label: () => t("Freigegeben", "Approved") },
  published: { art: "ok", label: () => t("Veröffentlicht", "Published") },
  reporting: { art: "ok", label: () => t("Veröffentlicht", "Published") },
  invoicing: { art: "ok", label: () => t("Veröffentlicht", "Published") },
  done: { art: "ok", label: () => t("Veröffentlicht", "Published") },
};

/**
 * Content der Kooperation aus ihren Beiträgen (0068): alle live bzw. freigegeben ✓; eingereicht, in Korrektur, bei der Marke
 * oder noch offen ◷ („2 von 4 live“); überfällig ! (Frist nur in laufenden Kooperationen und Monaten, wie app.deliverable_active);
 * keine Beiträge –. Ohne Einzelbeiträge zählt der Arbeitsstand der Kooperation (Monatsablauf mit Content-Frist):
 * Konzept, Produktion, Freigabe ◷, ab Freigabe ✓, überfällige Content-Frist !.
 */
export function contentStand(d: {
  /** Kooperation bzw. Monat der Agentur oder aus den Sichten des Creators (talent_deals, talent_deal_periods) */
  deal: { id: string; archived_at: string | null; status: string };
  beitraege: readonly BeitragLite[];
  periods: ReadonlyMap<string, { status: string; manual_status: string | null; month: string }>;
  beiMarke: ReadonlySet<string>;
  today: string;
  arbeitsstand: string;
  contentFristUeberfaellig?: boolean;
  /** Weg zur Kooperation, an den „?beitrag=“ angehängt wird (Agentur: /agentur/kooperationen/<id>) */
  basis: string | null;
}): Stand {
  const list = d.beitraege.filter((b) => b.status !== "cancelled");
  const zu = (b: BeitragLite | undefined): string | null => {
    if (!b || !d.basis) return null;
    const monat = b.period_id ? d.periods.get(b.period_id)?.month.slice(0, 7) : undefined;
    const q = new URLSearchParams();
    if (monat) q.set("monat", monat);
    q.set("beitrag", b.id);
    return `${d.basis}?${q.toString()}`;
  };
  if (!list.length) {
    if (d.contentFristUeberfaellig) return stand("haengt", t("Überfällig", "Overdue"));
    const aus = CONTENT_AUS_STAND[d.arbeitsstand];
    return aus ? stand(aus.art, aus.label()) : stand("leer", t("Noch keiner", "None yet"));
  }
  // Dieselbe Regel wie app.deliverable_active; die Stände sind in beiden Sichten dieselben Schlüssel
  const aktiv = (b: BeitragLite) => beitragAktiv(b, d.deal as Pick<DealFull, "archived_at" | "status">, (b.period_id ? d.periods.get(b.period_id) : undefined) as Pick<PeriodFull, "status" | "manual_status"> | undefined);
  const ueber = list.filter((b) => aktiv(b) && overdueKind(b, d.today));
  if (ueber.length) {
    const label = ueber.length > 1 ? t(`${ueber.length} überfällig`, `${ueber.length} overdue`)
      : overdueKind(ueber[0]!, d.today) === "publish" ? t("Posting überfällig", "Posting overdue") : t("Entwurf überfällig", "Draft overdue");
    return stand("haengt", label, zu(ueber[0]));
  }
  const n = list.length;
  const live = list.filter((b) => b.status === "live").length;
  const fertig = list.filter((b) => b.status === "live" || b.status === "approved").length;
  if (fertig === n) return stand("ok", live === n ? (n === 1 ? t("Live", "Live") : t(`Alle ${n} live`, `All ${n} live`)) : t("Freigegeben", "Approved"), n === 1 ? zu(list[0]) : null);
  const marke = list.find((b) => d.beiMarke.has(b.id));
  const pruefung = list.find((b) => b.status === "draft_submitted");
  const korrektur = list.find((b) => b.status === "changes_requested");
  const hinweis = marke ? t("Bei Marke", "With brand") : pruefung ? t("In Prüfung", "In review") : korrektur ? t("Korrektur", "Revision") : null;
  const zahl = t(`${live} von ${n} live`, `${live} of ${n} live`);
  return stand("laeuft", hinweis ? t(`${hinweis} · ${live}/${n} live`, `${hinweis} · ${live}/${n} live`) : zahl, zu(marke ?? pruefung ?? korrektur ?? (n === 1 ? list[0] : undefined)));
}

// ── Rechnung ──────────────────────────────────────────────────────────────────

const OHNE_WIRKUNG = new Set(["withdrawn", "discarded", "cancelled"]);

/**
 * Rechnung der Kooperation: Rechnung an die Marke bezahlt und (beim Weg über die Agentur) an den Creator ausgezahlt ✓;
 * offen, Entwurf, in Prüfung, Auszahlung bzw. Provision offen ◷; überfällig, Mahnung, Versand gescheitert, Auszahlung
 * überfällig oder angeforderte Creator-Rechnung über der Frist !; noch keine Rechnung –. Die Pillen der Rechnungen kommen
 * aus der Rechnungsansicht (dieselbe Regel wie die Seite „Rechnungen“), die fehlende Seite aus der Monatsabrechnung
 * (teilweise bezahlt). Stornierte, zurückgezogene und verworfene Rechnungen sowie Stornobelege zählen nicht.
 */
export function rechnungsStand(d: {
  monate: readonly MonatInfo[];
  ohneMonat: readonly InvoiceEntry[];
  /** Pille einer Rechnung wie auf der Seite „Rechnungen“; null = ohne Einstellungen der Agentur (dann der Rohstatus) */
  pille: (row: InvoiceRow) => Pill | null;
  today: string;
  /** Frist der Creator-Rechnung in Tagen nach der Anforderung (org_settings.requested_urgent_days) */
  fristTage?: number | null;
  timeZone: string;
  /** Recht „Beträge sehen“: ohne führt die Pille zur Kooperation statt in den Rechnungsbereich */
  betraege: boolean;
  basis: string | null;
}): Stand {
  const zuRechnung = (row: InvoiceRow | undefined) => (row && d.betraege ? `/agentur/rechnungen/${row.id}` : null);
  const gesehen = new Set<string>();
  const alle = [...d.monate.flatMap((m) => m.invoices), ...d.ohneMonat].filter((e) => !gesehen.has(e.row.id) && gesehen.add(e.row.id));
  const wirksam = alle.filter((e) => !OHNE_WIRKUNG.has(e.row.status) && !e.row.credit_for).map((e) => ({ e, p: d.pille(e.row) ?? invoicePillFallback(e.row.status) }));
  const monate = d.monate.filter((m) => !monatStorniert(m.period));
  const fehler = wirksam.find((x) => x.p.tone === "error");
  if (fehler) return stand("haengt", fehler.p.label, zuRechnung(fehler.e.row));
  // Angeforderte Rechnung des Creators über der Frist und noch nicht eingereicht (wie die nächste Frist der Kooperation)
  const fristUeber = monate.find((m) => {
    if (m.done) return false;
    const frist = creatorFrist(m.period, { tage: d.fristTage, timeZone: d.timeZone });
    if (!frist || frist >= d.today) return false;
    return !m.invoices.some((e) => isCreatorInvoice(e.row) && e.record && isLive(e.record) && e.record.reviewStatus !== "not_submitted");
  });
  if (fristUeber) return stand("haengt", t("Creator-Rechnung überfällig", "Creator invoice overdue"), d.basis ? `${d.basis}?monat=${fristUeber.month}` : null);
  if (!wirksam.length) {
    if (monate.length && monate.every((m) => m.done) && monate.some((m) => m.uebernommen)) return stand("ok", t("Abgerechnet", "Settled"));
    if (monate.some((m) => !m.done && m.period.invoice_requested_at)) return stand("laeuft", t("Angefordert", "Requested"));
    return stand("leer", t("Noch keine", "None yet"));
  }
  const einzig = wirksam.length === 1 ? wirksam[0]!.e.row : undefined;
  const markeOffen = wirksam.filter((x) => isBrandInvoice(x.e.row) && x.p.tone !== "success");
  if (markeOffen.length) {
    return stand("laeuft", markeOffen.length === 1 ? markeOffen[0]!.p.label : t(`${markeOffen.length} offen`, `${markeOffen.length} open`), zuRechnung(markeOffen.length === 1 ? markeOffen[0]!.e.row : einzig));
  }
  const andereOffen = wirksam.filter((x) => !isBrandInvoice(x.e.row) && x.p.tone !== "success");
  if (andereOffen.length) {
    const auszahlung = andereOffen.some((x) => x.e.row.kind === "talent_to_agency");
    return stand("laeuft", auszahlung ? t("Auszahlung offen", "Payout open") : t("Provision offen", "Commission open"), zuRechnung(andereOffen.length === 1 ? andereOffen[0]!.e.row : einzig));
  }
  // Alle vorhandenen Rechnungen bezahlt, aber eine nötige Seite fehlt noch (Monatsabrechnung „teilweise bezahlt“)
  const teil = monate.find((m) => TEILWEISE.has(m.status));
  if (teil) {
    const label = teil.status === "creator_paid_brand_open" ? t("Marke offen", "Brand open") : teil.status === "partially_paid_commission" ? t("Provision offen", "Commission open") : t("Auszahlung offen", "Payout open");
    return stand("laeuft", label, d.basis ? `${d.basis}?monat=${teil.month}` : null);
  }
  const erledigt = monate.filter((m) => m.done).length;
  if (erledigt === monate.length) return stand("ok", t("Bezahlt", "Paid"), zuRechnung(einzig));
  return stand("laeuft", t(`${erledigt}/${monate.length} Monate bezahlt`, `${erledigt}/${monate.length} months paid`));
}

// ── Kooperationen des Hubs ────────────────────────────────────────────────────

export interface HubKoop {
  id: string;
  titel: string;
  /** Kooperationsseite (Klick auf die Zeile) */
  href: string;
  marke: { id: string; name: string };
  creator: { id: string; name: string };
  kampagne: { id: string; titel: string } | null;
  /** Fällt unter „Nur aktive“ (nicht erledigt, nicht archiviert) */
  aktiv: boolean;
  vertrag: Stand;
  content: Stand;
  rechnung: Stand;
  suchtext: string;
}

export const haengt = (k: Pick<HubKoop, "vertrag" | "content" | "rechnung">): boolean => k.vertrag.art === "haengt" || k.content.art === "haengt" || k.rechnung.art === "haengt";

/** Content-Frist der Kooperation bzw. eines Monats überschritten (nächste Frist der Liste: „Content-Abgabe“, „Content Oktober“) */
export function contentFristUeberfaellig(z: Pick<KoopZeile, "frist">): boolean {
  return !!z.frist?.ueberfaellig && z.frist.art.startsWith("Content");
}

/** Kooperationen der Agentur (Zeilen der Liste) mit den drei Ständen; eine Abfrage je Bereich, nichts je Zeile. */
export function baueHubKoops(zeilen: readonly KoopZeile[], ctx: {
  beitraege: readonly BeitragLite[];
  beiMarke: ReadonlySet<string>;
  periods: readonly Pick<PeriodFull, "id" | "status" | "manual_status" | "month">[];
  lage?: VertragsLage | null;
  pille: (row: InvoiceRow) => Pill | null;
  today: string;
  now: number;
  timeZone: string;
  fristTage?: number | null;
  betraege: boolean;
  selbst?: boolean;
}): HubKoop[] {
  const periods = new Map(ctx.periods.map((p) => [p.id, p]));
  const jeDeal = new Map<string, BeitragLite[]>();
  for (const b of ctx.beitraege) jeDeal.set(b.deal_id, [...(jeDeal.get(b.deal_id) ?? []), b]);
  return zeilen.map((z) => {
    const basis = `/agentur/kooperationen/${z.deal.id}`;
    return {
      id: z.deal.id,
      titel: z.deal.title,
      href: basis,
      marke: { id: z.deal.brand_id, name: z.brandName },
      creator: { id: z.deal.talent_id, name: z.talentName },
      kampagne: z.kampagne ? { id: z.kampagne.id, titel: z.kampagne.title } : null,
      aktiv: !z.archiviert && !z.status.erledigt,
      vertrag: vertragsStand({ arbeitsstand: z.arbeitsstand, vertraege: z.vertraege, lage: ctx.lage, now: ctx.now, today: ctx.today, selbst: ctx.selbst }),
      content: contentStand({
        deal: z.deal, beitraege: jeDeal.get(z.deal.id) ?? [], periods, beiMarke: ctx.beiMarke, today: ctx.today, arbeitsstand: z.arbeitsstand,
        contentFristUeberfaellig: contentFristUeberfaellig(z), basis,
      }),
      rechnung: rechnungsStand({ monate: z.monate, ohneMonat: z.ohneMonat, pille: ctx.pille, today: ctx.today, fristTage: ctx.fristTage, timeZone: ctx.timeZone, betraege: ctx.betraege, basis }),
      suchtext: z.suchtext,
    };
  });
}

// ── Filter ────────────────────────────────────────────────────────────────────

export function passtHubSuche(k: Pick<HubKoop, "suchtext" | "titel" | "marke" | "creator" | "kampagne">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const text = `${k.suchtext} ${k.titel} ${k.marke.name} ${k.creator.name} ${k.kampagne?.titel ?? ""}`.toLowerCase();
  return q.split(/\s+/).every((teil) => text.includes(teil));
}

/** Suche und „Nur aktive“; der Zähler „n hängen“ rechnet mit derselben Menge, bevor „nur hängende“ greift. */
export function filtereHub(koops: readonly HubKoop[], f: { suche: string; nurAktiv: boolean; nurHaengend: boolean }): { koops: HubKoop[]; haengend: number } {
  const vorauswahl = koops.filter((k) => (!f.nurAktiv || k.aktiv) && passtHubSuche(k, f.suche));
  const haengend = vorauswahl.filter(haengt).length;
  return { koops: f.nurHaengend ? vorauswahl.filter(haengt) : vorauswahl, haengend };
}

// ── Blickwinkel ───────────────────────────────────────────────────────────────

export type Blick = "marke" | "creator" | "kampagne";
export const BLICKE: readonly Blick[] = ["marke", "creator", "kampagne"];
export const istBlick = (v: string | null): v is Blick => !!v && (BLICKE as readonly string[]).includes(v);

export interface HubZeile {
  koop: HubKoop;
  /** fett: Creator (nach Marke, nach Kampagne) bzw. Marke (nach Creator) */
  haupt: string;
  /** darunter: „Kampagne X“ bzw. der Titel der Kooperation; leer, wenn es nichts hinzuzufügen gibt */
  neben: string;
}

export interface HubKarte {
  key: string;
  blick: Blick;
  titel: string;
  unter: string;
  initialen: string;
  /** Creator rund, Marke und Kampagne eckig */
  rund: boolean;
  /** Sammelkarte „Ohne Kampagne“ einer Marke */
  ohneKampagne: boolean;
  zeilen: HubZeile[];
  haengend: number;
}

const kampagnen = (n: number) => (n === 0 ? t("Keine Kampagne", "No campaign") : n === 1 ? t("1 Kampagne", "1 campaign") : t(`${n} Kampagnen`, `${n} campaigns`));
/** Im Creator-Konto (Studio, ta) und im Creator-Portal (deals) heißen Kooperationen „Deals“ */
const kooperationen = (n: number, deals = false) => (deals ? (n === 1 ? t("1 Deal", "1 deal") : t(`${n} Deals`, `${n} deals`))
  : n === 1 ? ta("1 Kooperation", "1 deal", "1 Deal", "1 deal") : ta(`${n} Kooperationen`, `${n} deals`, `${n} Deals`, `${n} deals`));
const marken = (n: number) => (n === 1 ? t("1 Marke", "1 brand") : t(`${n} Marken`, `${n} brands`));
const creatorZahl = (n: number) => t(`${n} Creator`, n === 1 ? "1 creator" : `${n} creators`);
const kampagnenZeile = (k: HubKoop) => (k.kampagne ? t(`Kampagne ${k.kampagne.titel}`, `Campaign ${k.kampagne.titel}`) : k.titel);

function sortiereZeilen(z: HubZeile[]): HubZeile[] {
  return z.sort((a, b) => Number(haengt(b.koop)) - Number(haengt(a.koop)) || a.haupt.localeCompare(b.haupt, "de") || a.neben.localeCompare(b.neben, "de"));
}

/** Karten mit „hängt“ zuerst, sonst alphabetisch; „Ohne Kampagne“ nach den Kampagnen derselben Gruppe. */
function sortiereKarten(k: HubKarte[]): HubKarte[] {
  return k.sort((a, b) => Number(b.haengend > 0) - Number(a.haengend > 0) || Number(a.ohneKampagne) - Number(b.ohneKampagne) || a.titel.localeCompare(b.titel, "de") || a.unter.localeCompare(b.unter, "de"));
}

function gruppiere(koops: readonly HubKoop[], key: (k: HubKoop) => string): Map<string, HubKoop[]> {
  const m = new Map<string, HubKoop[]>();
  for (const k of koops) {
    const s = key(k);
    m.set(s, [...(m.get(s) ?? []), k]);
  }
  return m;
}

/**
 * Karten eines Blickwinkels. Nach Kampagne: je Kampagne eine Karte; Kooperationen ohne Kampagne je Marke gesammelt unter
 * „Ohne Kampagne“ (eine Karte je Kooperation würde die Seite fluten, eine einzige Sammelkarte die Marke verlieren).
 */
export function hubKarten(koops: readonly HubKoop[], blick: Blick, opts: { ohneKampagnen?: boolean; deals?: boolean; ohneCreator?: boolean } = {}): HubKarte[] {
  const karten: HubKarte[] = [];
  // Nur ein Creator (Creator-Konto, Creator-Portal): statt des immer gleichen Namens steht der Titel der Kooperation vorn
  const zeile = (k: HubKoop, neben: string): HubZeile => (opts.ohneCreator
    ? { koop: k, haupt: k.titel, neben: blick === "marke" && k.kampagne ? kampagnenZeile(k) : "" }
    : { koop: k, haupt: k.creator.name, neben });
  if (blick === "marke") {
    for (const [id, list] of gruppiere(koops, (k) => k.marke.id)) {
      const name = list[0]!.marke.name;
      const nk = new Set(list.map((k) => k.kampagne?.id).filter(Boolean)).size;
      karten.push({
        key: `marke:${id}`, blick, titel: name, unter: opts.ohneKampagnen ? kooperationen(list.length, opts.deals) : `${kampagnen(nk)} · ${kooperationen(list.length, opts.deals)}`, initialen: initials(name), rund: false, ohneKampagne: false,
        zeilen: sortiereZeilen(list.map((k) => zeile(k, kampagnenZeile(k)))), haengend: list.filter(haengt).length,
      });
    }
  } else if (blick === "creator") {
    for (const [id, list] of gruppiere(koops, (k) => k.creator.id)) {
      const name = list[0]!.creator.name;
      const nm = new Set(list.map((k) => k.marke.id)).size;
      karten.push({
        key: `creator:${id}`, blick, titel: name, unter: `${marken(nm)} · ${kooperationen(list.length, opts.deals)}`, initialen: initials(name), rund: true, ohneKampagne: false,
        zeilen: sortiereZeilen(list.map((k) => ({ koop: k, haupt: k.marke.name, neben: kampagnenZeile(k) }))), haengend: list.filter(haengt).length,
      });
    }
  } else {
    for (const [key, list] of gruppiere(koops, (k) => (k.kampagne ? `kampagne:${k.kampagne.id}` : `ohne:${k.marke.id}`))) {
      const erste = list[0]!;
      const ohne = !erste.kampagne;
      const titel = erste.kampagne?.titel ?? t("Ohne Kampagne", "No campaign");
      const nc = new Set(list.map((k) => k.creator.id)).size;
      karten.push({
        key, blick, titel, unter: `${erste.marke.name} · ${creatorZahl(nc)}`, initialen: initials(ohne ? erste.marke.name : titel), rund: false, ohneKampagne: ohne,
        zeilen: sortiereZeilen(list.map((k) => zeile(k, k.titel))), haengend: list.filter(haengt).length,
      });
    }
  }
  return sortiereKarten(karten);
}

/** Aufgeklappt zu Beginn: bei wenigen Karten alle, bei vielen nur die, in denen etwas hängt; eine Suche klappt alle Treffer auf. */
export function standardOffen(karte: Pick<HubKarte, "haengend">, anzahlKarten: number, suche: string): boolean {
  return !!suche.trim() || anzahlKarten <= 8 || karte.haengend > 0;
}

/** Kopf-Pille einer Karte: „2 hängen“ rot bzw. „alles im Plan“ grün */
export function kartenPill(k: Pick<HubKarte, "haengend">): Pill {
  return k.haengend > 0
    ? standPill({ art: "haengt", label: k.haengend === 1 ? t("1 hängt", "1 stuck") : t(`${k.haengend} hängen`, `${k.haengend} stuck`) })
    : standPill({ art: "ok", label: t("alles im Plan", "all on track") });
}
